/*
This module provides the main data model for the available and selected PICO attributes in the filter panes.
This updates the available characteristics, treatments, and outcomes as a query changes and handles
changes to the selected PICO attributes as well. Note that there are some difference in behavior
between the build-a-question entry mode and the main study list view.
*/
import { get, post } from 'tools/request'
import { all, reactive, VArray, VMap, VNumber } from 'alkali'
import app from 'app/app'
import { index, Cache } from 'tools/Index'
import { CONTROL_IDS } from 'analysis/assignGroupArms'
import { questionToURIComponent } from 'analysis/Question'
import { DEMOGRAPHIC_IDS } from 'util/characteristics'
import {
	analysisType,
	askingQuestion,
	demographics,
	studyFilter,
	structuredQuery,
	analysisSelections,
	cooccurrences,
	includeFlagged,
	outcomeSettings,
	onExecuteSearchActions,
	isEntryPage,
	liveParsed,
	sorting,
	currentQuestion,
	questionAsParameters,
	groupInterventions,
	updateCooccurrences,
	isAnalysisOutcomePositive,
	docSearchQuery,
	navigating
} from './SearchQuery'

export const selectedOutcomeIds = window.selectedOutcomeIds = reactive([])

export const selectedTreatmentIds = window.selectedTreatmentIds = reactive([])

export const selectedCharacteristicIds = reactive([])

export const stableSelectedCharacteristicIds = reactive([]) // selectedCharacteristicIds but only changes if it is different than the studiesSearch results and changed by the user

selectedCharacteristicIds.subscribe(() => {
	if (differentArray(selectedCharacteristicIds.valueOf(), stableSelectedCharacteristicIds.valueOf())) {
		stableSelectedCharacteristicIds.put(selectedCharacteristicIds.valueOf())
		// pushState
	}
})

export const evidenceCache = new Map()
export const studySearchCache = new Map()

function clearSelections() {
	selectedOutcomeIds.is([])
	selectedTreatmentIds.is([])
	selectedCharacteristicIds.is([])
}
onExecuteSearchActions.push(clearSelections)

export const studiesSearch = all([structuredQuery, sorting, demographics, studyFilter,
		includeFlagged, analysisType, outcomeSettings, docSearchQuery,
		analysisSelections.settings.multiGroupPooling, analysisSelections.settings.multiGroupPooling, analysisSelections.settings.continuityCorrection, analysisSelections.settings.confidenceLevel,
		analysisSelections.settings.useSMD, analysisSelections.property('adjustments') ],
		(structuredQuery, sorting, demographics, studyFilter) => {
	return searchForStudies(0)
})

let currentStudiesRequest
let lastQuestion

export async function searchForStudies(offset, studyRequest, limit) {
	if (!structuredQuery.valueOf() || navigating)
		return {
			studies: [],
		}
	let { outcomes, interventions, characteristics, isAsking } = structuredQuery.valueOf()

	for (let characteristic of characteristics) {
		cumulativeAllCharacteristics.set(characteristic.id ? characteristic.id.split(',')[0] : characteristic.label, characteristic)
	}

	let { importCollection } = studyFilter.valueOf() || {}
	
	if (interventions.length == 0 && characteristics.length == 0 && (isAsking ||!importCollection &&
			!location.search.match(/query=([^&]+)/) && !analysisSelections.valueOf()?.dsQuery)) {
		if (!isAsking)
			alert('A condition or treatment is required to do a full search')
		if (window.isCovid) {
			return {
				characteristics: [
					{ id: 'SUT2bX', label: 'COVID-19' },
					{ id: 'S1', label: 'Elderly' },
					{ id: 'S2', label: 'Smoker'},
				],
				interventions: [
					{ id: 'SUQUtc', label: 'Remdesivir' },
					{ id: 'SUER8o', label: 'Lopinavir'},
					{ id: 'SUE9x', label: 'Hydroxychloroquine' },
				],
				outcomes: [
					{ id: 'SGHa0,SUCE7F/SUyGb', label: 'Virological Response' },
					{ id: 'SUE-_S', label: 'Mortality' },
					{ id: 'SUaZ', label: 'Adverse Events' },
				],
				filtered: {},
				studies: [],
				selectedInterventionIds: [],
				selectedOutcomeIds: [],
			}			
		}
		return {
			characteristics: isEntryPage ? [
				{ id: 'SUT2bX', label: 'COVID-19' },
				{ id: 'SUBjMu', label: 'Plaque Psoriasis' },
				{ id: 'SUChr', label: "Crohns Disease"},
			] : [],
			interventions: isEntryPage ? [
				{ id: 'SUQUtc', label: 'Remdesivir' },
				{ id: 'SU0MK', label: 'Tiotropium' },
				{ id: 'SUER8o', label: 'Adalimumab'},
			] : [],
			outcomes: isEntryPage ? [
				{ id: 'SGHFB', label: 'Clinical Remission' },
				{ id: 'SUE-_S', label: 'Mortality' },
				{ id: 'SUaZ', label: 'Adverse Events' },
			] : [],
			filtered: {},
			studies: [],
			selectedInterventionIds: [],
			selectedOutcomeIds: [],
		}
	}
	let url = await questionAsParameters()

	if (isAsking) {
		url += '&buildAQuestion=true'
	} else {
		let question = questionToURIComponent(liveParsed.annotations.valueOf())
		let isNew = lastQuestion != question
		lastQuestion = question
		if (isNew)
			url += '&isNewQuestion=true'	
	}
	if (sorting.valueOf()) {
		if (sorting.recency != 5)
			url += '&recency=' + sorting.recency
		if (sorting.participants != 0)
			url += '&participants=' + sorting.participants
		if (sorting.analyzable != 50)
			url += '&analyzable=' + sorting.analyzable
		if (sorting.design != 50)
			url += '&rob=' + sorting.design
	}
	if (offset)
		url += '&offset=' + offset
	if (limit)
		url += '&limit=' + limit
	if (studySearchCache.has(url) && !studyRequest)
		return studySearchCache.get(url)
	const removeAnalysisType = url => url.replace(/analysisType=\w+/, '')
	if (currentStudiesRequest && removeAnalysisType(currentStudiesRequest.url) != removeAnalysisType(url))
		currentStudiesRequest.abort()
	currentStudiesRequest = studyRequest ? studyRequest(url) : get('portal/studies?' + url)
	currentStudiesRequest.url = url
	studySearchCache.set(url, currentStudiesRequest)
	for (let outcome of outcomes || []) {
		studySearchCache.set(+outcome || outcome, currentStudiesRequest)
	}
	return currentStudiesRequest.then(studyData => {
		for (let outcome of studyData.selectedOutcomeIds || [])
			studySearchCache.set(outcome, Promise.resolve(studyData))
		addAttributeChildren(studyData.outcomes || [], cumulativeAllOutcomes)
		addAttributeChildren(studyData.characteristics || [], cumulativeAllCharacteristics)
		return studyData
	}, error => {
		if (error.status != 401 && !error.isCanceled)
			alert(error.toString())
		studySearchCache.delete(url)
		return {
			studies: [],
		}
	})
}
export const evidenceSearch = all([structuredQuery, sorting], (structuredQuery, sorting) => {
	if (!structuredQuery.valueOf())
		return {
			estimates: [],
			characteristics: [],
		}
	let { outcomes, interventions, characteristics, study } = structuredQuery.valueOf()
	/*if (selectedOutcomeIds.length > 0)
		outcomes = selectedOutcomeIds
	if (selectedCharacteristicIds.length > 0)
		characteristics = selectedCharacteristicIds
	if (filterTreatmentIds.length > 0)
		interventions = filterTreatmentIds
	else if (interventions.length > 0) {
		stableSelectedTreatmentIds.value = interventions
		selectedTreatmentIds.put(interventions)
	}*/

	if (outcomes.length == 0 && interventions.length == 0 && characteristics.length == 0)
		throw new Error('There is nothing to search for')
	const encodePart = values => values.map(value => encodeURIComponent(value.id.split(',')[0])).join(';')
	let url = 
		'/portal/analysis/nma?outcomes=' +
			encodePart(outcomes) +
			'&interventions=' +
			encodePart(interventions) +
			'&characteristics=' +
			encodePart(characteristics)
	if (evidenceCache.has(url))
		return evidenceCache.get(url)
	let response = post(url)
	response.then(evidenceData => {
		for (let outcome of evidenceData.selectedOutcomes)
			evidenceCache.set(outcome, Promise.resolve(evidenceData))
	})
	
	evidenceCache.set(url, response)	
	for (let outcome of outcomes) {
		evidenceCache.set(+outcome || outcome, response)
	}
	return response
})

export const evidenceStats = studiesSearch.property('filtered')

export const cumulativeAllOutcomes = new Cache()

export const selectedOutcomes = window.selectedOutcomes = all([studiesSearch, structuredQuery, isAnalysisOutcomePositive], (studyData, structuredQuery, isAnalysisOutcomePositive) => {
	if (!studyData)
		return []
	for (let outcome of studyData.outcomes || []) {
		cumulativeAllOutcomes.set(outcome.id, outcome)
	}
	let selectedOutcomes = studyData.selectedOutcomes || []

	let existingOutcomeIds = (structuredQuery.valueOf()?.outcomes || []).flatMap(outcome => outcome.ids || outcome.id || outcome.label)
	let newSelectedOutcomeIds = (existingOutcomeIds.length > 0 ? existingOutcomeIds :
		studyData.selectedOutcomeIds || []).map(id => id.split(',')[0]).filter((id, i) =>
		/^S[UG0-9]/.test(id) || i == 0)
	for (let outcome of selectedOutcomes) {
		if (typeof isAnalysisOutcomePositive?.[outcome.id] == 'boolean')
			outcome.isPositive = isAnalysisOutcomePositive[outcome.id]
	}
	selectedOutcomes = selectedOutcomes.length ? selectedOutcomes :
		newSelectedOutcomeIds.map(selectedOutcomeId =>
			cumulativeAllOutcomes.get(selectedOutcomeId.split('/')[0]) || cumulativeAllOutcomes.get(selectedOutcomeId))/*studyData.outcomes.find(outcome =>
			selectedOutcomeId.startsWith(outcome.id))*/
		/*let commonOutcome = rootOutcome ?
			rootOutcome.children?.find &&
				rootOutcome.children.find(outcome => outcome.id == selectedOutcomeId) ||
				rootOutcome : cumulativeAllOutcomes.get(selectedOutcomeId)
		if (typeof isAnalysisOutcomePositive?.[selectedOutcomeId] == 'boolean') {
			commonOutcome.isPositive = isAnalysisOutcomePositive[selectedOutcomeId]
		}
		return commonOutcome
	}).filter(outcome => outcome)*/
	selectedOutcomeIds.value = newSelectedOutcomeIds
	selectedOutcomeIds.updated()
	return selectedOutcomes.slice(0, 1) // FIXME: once we have multiple outcomes, get rid of this
}).as(VArray)

let outcomeIdUpdate
selectedOutcomeIds.put = (updatedOutcomeIds) => {
	selectedOutcomeIds.value = updatedOutcomeIds
	selectedOutcomeIds.updated()
	clearTimeout(outcomeIdUpdate)
	outcomeIdUpdate = setTimeout(() => {
		structuredQuery.outcomes.put(
			updateAttributesFromIds(structuredQuery.outcomes.valueOf(), updatedOutcomeIds, id => {
				return cumulativeAllOutcomes.get(id)
			}, 'outcome'))
	})
}

const cumulativeAllCharacteristics = new Cache()

export const selectedCharacteristics = structuredQuery.to(({ characteristics }) => {
	let unionOfCharacteristics = characteristics.flatMap(characteristic => {
		let union = []
		do {
			union.push(characteristic)
		} while (characteristic = characteristic.or)
		return union
	})
	selectedCharacteristicIds.value = unionOfCharacteristics.flatMap(characteristic =>
		characteristic.ids || (characteristic.id ? [ characteristic.id ] : [ characteristic.label?.toLowerCase() ])).filter((id, i) =>
		/^S[UG0-9]/.test(id) || i == 0)
	selectedCharacteristicIds.updated()
	return unionOfCharacteristics
}).as(VArray)

let characteristicIdUpdate
selectedCharacteristicIds.put = (updatedCharacteristicIds) => {
	selectedCharacteristicIds.value = updatedCharacteristicIds
	selectedCharacteristicIds.updated()
	structuredQuery.characteristics.put(
		updateAttributesFromIds(structuredQuery.characteristics.valueOf(), updatedCharacteristicIds, id => {
			return cumulativeAllCharacteristics.get(id) || { id, root: id.split('/')[0] }
		}, 'condition'))
	characteristicsUpdated()
}
export function characteristicsUpdated() {
	clearTimeout(characteristicIdUpdate)
	characteristicIdUpdate = setTimeout(() => {
		if (askingQuestion && selectedCharacteristicIds.valueOf().length == 1) {
			updateCooccurrences()
			characteristicsAvailable.updated()
		}
		interventionsAvailable.updated()
		outcomesAvailable.updated()
	})
}
function updateAttributesFromIds(attributes, ids, getAttribute, type) {
	let remainingIds = ids.slice(0)
	let newAttributeSet = []
	for (let attribute of attributes) {
		let anded = true
		do {
			let attributeIds = attribute.ids || [ attribute.id || attribute.label.toLowerCase() ]
			for (let id of attributeIds) {
				let position = remainingIds.indexOf(id)
				if (position > -1) {
					remainingIds.splice(position, 1)
					attribute.fromText = false
					if (!newAttributeSet.includes(attribute))
						newAttributeSet.push(attribute)
					attribute.anded = anded
					let newIds = attributeIds.filter(id => ids.includes(id))
					if (newIds.join(',') != attributeIds.join(',') && attribute.ids) {
						attribute.ids = newIds
						attribute.fromText = attribute.tentative = false
					}
				}
			}
			anded = false
		} while (attribute = attribute.or)
	}
	for (let id of remainingIds) {
		let attribute = getAttribute(id)
		attribute.anded = DEMOGRAPHIC_IDS.includes(newAttributeSet[newAttributeSet.length - 1]?.id)
		attribute.fromText = attribute.tentative = false
		attribute.ids = [ id ]
		if (!newAttributeSet.includes(attribute)) {
			if (DEMOGRAPHIC_IDS.includes(id)) {
				newAttributeSet.unshift(attribute)
				attribute.anded = true
			} else
				newAttributeSet.push(attribute)
		}
	}
	let newAttributes = []
	let lastAttribute
	for (let attribute of newAttributeSet) {
		attribute.type = type
		if (!lastAttribute || attribute.anded) {
			newAttributes.push(attribute)
			lastAttribute = attribute
			lastAttribute.or = null
		}
		else {
			lastAttribute.or = attribute
			attribute.or = null
			lastAttribute = attribute
		}
	}
	return newAttributes
}

export const cumulativeAllTreatments = new Cache()

export const selectedTreatments = window.selectedTreatments = structuredQuery.to(({ interventions }) => analysisType.to(async currentAnalysisType => {
	let lastLabel
	let selectedTreatments = interventions.filter(intervention => {
		if (intervention.id?.startsWith('S') || intervention.label != lastLabel) {
			lastLabel = intervention.label
			return true
		}
	})
	if (interventions.length == 0 || currentAnalysisType == 'direct' || currentAnalysisType == 'cohort') {
		if (selectedTreatments.length == 0) { // if none specified by user, let the server pick
			let studyData = await studiesSearch
			let allInterventions = studyData.interventions || []
			for (let intervention of allInterventions) {
				cumulativeAllTreatments.set(intervention.id, intervention)
			}
			selectedTreatments = (studyData.selectedInterventionIds || []).map(id =>
				cumulativeAllTreatments.get(id) || { id })
		}
		if (structuredQuery.interventions.valueOf() != interventions)
			return selectedTreatments // it has been updated while we are waiting, just return
	} else {
		selectedTreatments = interventions.slice(0, 4)
		for (let intervention of structuredQuery.interventions) {
			if (!selectedTreatments.find(treatment => intervention.id == treatment.id)) {
				selectedTreatments.push(intervention)
			}
		}
	}
	let treatmentIds = selectedTreatments.flatMap(treatment => treatment.ids || [ treatment.id || treatment.label.toLowerCase() ])
/*	let senseIds = treatmentIds.filter(id => /^S[UG0-9]/.test(id))
	if (senseIds.length > 0)
		treatmentIds = senseIds*/

	if (selectedTreatments.length > 2 && currentAnalysisType == 'direct' || selectedTreatments.length > 1 && currentAnalysisType == 'cohort') {
		// switch to auto mode
		analysisType.put('auto')
	}
	if (selectedTreatmentIds.valueOf().length != treatmentIds.length) {
		selectedTreatmentIds.value = treatmentIds // assign the value without going through put
		selectedTreatmentIds.updated()
	}
	return selectedTreatments
})).as(VArray)

export const groupedInterventions = selectedTreatments.to(groupInterventions).as(VArray)

let treatmentIdUpdate
selectedTreatmentIds.put = (updatedTreatmentIds) => {
	let interventions = structuredQuery.get('interventions')
	let remainingTreatmentIds = updatedTreatmentIds.slice(0)
	interventions = interventions.filter(intervention => {
		let newIds = (intervention.ids || [ intervention.id ]).filter(id => {
			if (updatedTreatmentIds.includes(id)) {
				if (remainingTreatmentIds.includes(id))
					remainingTreatmentIds.splice(remainingTreatmentIds.indexOf(id), 1)
				return true
			}
		})
		if (newIds.some(id => /^S[UG0-9]/.test(id) || id == intervention.id)) {
			if (intervention.ids) {
				intervention.ids = newIds
				intervention.fromText = intervention.tentative = false
			}
			return true
		}
		if (updatedTreatmentIds.includes(intervention.id))
			return true
	})
	for (let id of remainingTreatmentIds) {
		let treatment = cumulativeAllTreatments.get(id) || { id }
		let intervention = interventions.find(intervention => intervention.label == treatment.label)
		if (intervention) {
			intervention.ids.push(id)
			intervention.ids.sort((a, b) => (/^S[UG0-9]/.test(b) ? 1 : 0) - (/^S[UG0-9]/.test(a) ? 1 : 0)) // put full-text at the end
		}
		else {
			if (CONTROL_IDS.includes(treatment.id))
				interventions.push(Object.assign({}, treatment, { label: 'Control' }))
			else
				interventions.push(treatment)
		}
	}
	if (analysisType == 'direct' || analysisType == 'cohort') {
		let truncate
		if (analysisType == 'direct' && interventions.length > 2) {
			if (confirm('You have selected more than two intervention groups, and direct analysis only supports the comparison of two, do you want to switch to automatic analysis so network analysis can be performed?'))
				analysisType.put('auto')
			else
				truncate = true
		}
		if (analysisType == 'cohort') {
			truncate = true
		}
		// trim back to one or two groups
		if (truncate) {
			interventions = interventions.slice(analysisType == 'direct' ? -2 : -1)
		}
	}
	structuredQuery.set('interventions', interventions)
//	treatmentIdUpdate = setTimeout(() => {
		if (askingQuestion && updatedTreatmentIds.length == 1) {
			updateCooccurrences()
		} else {
			characteristicsAvailable.updated()
			outcomesAvailable.updated()
		}
		analysisSelections.analyzeAs.put(analysisSelections.analyzeAs.valueOf()?.filter(analyzeAs => {
			// filter out analyzeAs configuration if interventions have been removed
			if (interventions.some(intervention => (intervention.ids || [ intervention.id ]).includes(analyzeAs.id)) ||
					typeof analyzeAs.id == 'number') // still exists or is intervention grouping (number)
				return true
		}))
//	})
	selectedTreatmentIds.value = updatedTreatmentIds
	selectedTreatmentIds.updated()
}

function addAttributeChildren(attributes, cumulativeAllAttributes) {
	for (let attribute of attributes) {
		let existingAttribute = cumulativeAllAttributes.get(attribute.id)
		if (existingAttribute) {
			for (let key in attribute) {
				if (!existingAttribute[key])
					existingAttribute[key] = attribute[key]
				
			}
		} else {
			cumulativeAllAttributes.set(attribute.id, attribute)
			if (attribute.related) {
				for (let id of attribute.related) {
					existingAttribute = cumulativeAllAttributes.get(id)
					if (existingAttribute) {
						let children = existingAttribute.children || (existingAttribute.children = [])
						children.push(attribute)
						attributes.splice(attributes.indexOf(attribute), 1)
						break
					}
				}
			}
		}
		if (attribute.children) {
			if (attribute.children.resolveChildren)
				continue // already assigned a promise
			for (let child of attribute.children) {
				cumulativeAllAttributes.set(child.id, child)
			}
		}
		if (!existingAttribute)
			continue
		if (existingAttribute.children) {
			if (existingAttribute.children.resolveChildren && attribute.children instanceof Array) {
				existingAttribute.children.resolveChildren(attribute.children)
				existingAttribute.children = attribute.children
			}
		} else {
			if (attribute.children)
				existingAttribute.children = attribute.children
/*			else {
				let resolveChildren
				attribute.children = new Promise(resolve => resolveChildren = resolve)
				attribute.children.resolveChildren = resolveChildren
			}*/
		}
	}
}

export const outcomesAvailable = cooccurrences.to(cooccurrences => studiesSearch.then(evidenceData => {
	let outcomeCooccurrences = _.keyBy(cooccurrences.outcomes, 'id')
	let existingOutcomeIds = selectedOutcomeIds.valueOf()
	let allOutcomes = evidenceData.outcomes ? evidenceData.outcomes : []
	let getRootId = (outcome) => outcome.id.split(',')[0]
	let outcomesByRoot = _.keyBy(allOutcomes, getRootId)
	for (let outcome of allOutcomes) {
		if (outcome.score)
			continue // already scored
		let cooccurrences = outcomeCooccurrences[getRootId(outcome)]
		outcome.score = cooccurrences ? cooccurrences.score : 0
	}
	for (let outcome of cooccurrences.outcomes) {
		if (!outcomesByRoot[outcome.id])
			allOutcomes.push(outcome)
	}

	allOutcomes = _.sortBy(allOutcomes, outcome => (outcome.count || 0) * 400 +
		(outcome.score || 0) + (outcome.children?.length ? 600 : 0) + (existingOutcomeIds.some(id => id.split('/')[0] == outcome.id) ? 100000 : 0)).reverse()
	for (let outcome of allOutcomes) {
		if (outcome?.children?.length) {
			for (let child of outcome.children) {
				cumulativeAllOutcomes.set(child.id, child)
				if (!outcome.type) {
					outcome.type = child.type
				}
				if (!outcome.minMax) {
					outcome.minMax = child.minMax
				}
			}
		}
	}
	addAttributeChildren(allOutcomes, cumulativeAllOutcomes)
	return allOutcomes
}))
.as(VArray)

export function addOutcome(term, doUpdate?) {
	cumulativeAllOutcomes.set(term.id.split(',')[0], term)
	outcomesAvailable.then(outcomes => {
		let existing = outcomes.find(outcome => outcome.id == term.id)
		if (existing)
			outcomes.splice(outcomes.indexOf(existing), 1)
		outcomes.unshift(term)
	})
	structuredQuery.outcomes.put([term])
	if (doUpdate)
		outcomesAvailable.updated()
}

const CHARACTERISTIC_BLOCKLIST = ['Age', 'Gender', 'Sex']
export const characteristicsAvailable = cooccurrences.to(cooccurrences => studiesSearch.then(studyData => {
	let evidenceCharacteristics = (studyData.characteristics || []).map(characteristic => {
		let previousChildren = cumulativeAllCharacteristics.get(characteristic.id)?.children
		if (previousChildren && !characteristic.children)
			characteristic.children = previousChildren
		cumulativeAllCharacteristics.set(characteristic.id, characteristic)
		characteristic.qualifiers = null
		return characteristic
	})
	let evidenceCharacteristicsById = {}
	addAttributeChildren(evidenceCharacteristics, cumulativeAllCharacteristics)

	return evidenceCharacteristics.concat(cooccurrences.characteristics).filter(characteristic => {
		if (!(CHARACTERISTIC_BLOCKLIST.includes(characteristic.label) || evidenceCharacteristicsById[characteristic.id])) {
			evidenceCharacteristicsById[characteristic.id] = true
			return true
		}
	}).slice(0, 15)
}))
.as(VArray)

export function addCharacteristic(term, doUpdate?) {
	term.type = 'condition'
	cumulativeAllCharacteristics.set(term.id.split(',')[0], term)
	characteristicsAvailable.then(characteristics => {
		let existing = characteristics.find(characteristic => characteristic.id == term.id)
		if (existing)
			characteristics.splice(characteristics.indexOf(existing), 1)
		characteristics.unshift(term)
	})
	let characteristics: {}[] = structuredQuery.characteristics.valueOf()
	if (DEMOGRAPHIC_IDS.includes(term.id)) {
		if (!characteristics)
			characteristics = []
		characteristics.unshift(term)
	} else if (characteristics?.[0]) {
		let lastCharacteristic = characteristics[characteristics.length - 1]
		if (DEMOGRAPHIC_IDS.includes(lastCharacteristic.id))
			characteristics.push(term)
		else {
			while (lastCharacteristic.or)
				lastCharacteristic = lastCharacteristic.or
			lastCharacteristic.or = term
		}
	} else
		characteristics = [ term ]
	
	structuredQuery.characteristics.put(characteristics)
	characteristicsUpdated()
}

export const interventionsAvailable = cooccurrences.to(cooccurrences => studiesSearch.then(studyData => {
	let interventionCooccurrences = index(cooccurrences.interventions, intervention => intervention.id)
	for (let intervention of cooccurrences.interventions) {
		cumulativeAllTreatments.set(intervention.id, intervention)
	}
	for (let intervention of studyData.interventions || []) {
		cumulativeAllTreatments.set(intervention.id, intervention)
		if (intervention.children) {
			for (let child of intervention.children) {
				cumulativeAllTreatments.set(child.id, child)
			}
		}
	}
	let requestedTreatments = structuredQuery.interventions.valueOf()?.slice(0)
	let treatments = studyData.interventions || []
	for (let treatment of treatments) {
		let treatmentKey = treatment.id
		let cooccurrence = interventionCooccurrences.get(treatmentKey)
		treatment.score = (cooccurrence ? cooccurrence.score / 250 : 0) + treatment.count + 10
		if (cooccurrence)
			interventionCooccurrences.delete(treatmentKey)
	}
	treatments = treatments.concat(Array.from(interventionCooccurrences.values()). map(treatment => {
		treatment.score = treatment.score / 250
		return treatment
	}))
	// merge in any ones that were requested, but not already included
	for (let requestedTreatment of requestedTreatments) {
		if (!requestedTreatment.id)
			requestedTreatment.id = requestedTreatment.label.toLowerCase() // full-text id
		let treatment = treatments.find(treatment => requestedTreatment.id.split(',')[0].split('/')
			.some(id => treatment.id.split('/').includes(id)))
		if (treatment)
			treatment.isSelected = true
		else if (!requestedTreatment.alternate) {
			requestedTreatment.isSelected = true
			treatments.push(requestedTreatment)
		}
	}
	treatments.forEach(treatment => treatment.score = treatment.count ? treatment.count * 20 : treatment.score || 0)
	return treatments.sort((a, b) => {
		let aScore = a.score + (a.isSelected ? 1000000000 : 0)
		let bScore = b.score + (b.isSelected ? 1000000000 : 0)
		return aScore > bScore ? -1 : aScore < bScore ? 1 : 0
	}).slice(0, 20)
}))
.as(VArray)

export function addTreatment(term) {
	cumulativeAllTreatments.set(term.id.split(',')[0], term)
	interventionsAvailable.then(interventions => {
		let existing = interventions.find(intervention => intervention.id == term.id)
		if (existing)
			interventions.splice(interventions.indexOf(existing), 1)
		interventions.unshift(term)
	})
	selectedTreatmentIds.put([ // add the new id, but if there are no explicit interventions (only auto-selected), clear them
		...(structuredQuery.valueOf()?.interventions?.length > 0 ? selectedTreatmentIds.valueOf() : []), term.id.split(',')[0]])
	if (askingQuestion && selectedTreatmentIds.valueOf().length == 1) {
		updateCooccurrences()
		interventionsAvailable.updated()
	}
	characteristicsAvailable.updated()
	outcomesAvailable.updated()
}

export const allOutcomes = studiesSearch.to(({ outcomes }) => Object.values(outcomes || {}))

const abbreviatedGroupName = (name) => (name.slice(0, 1) + name.slice(1).replace(/[aeiou]/g, '').slice(0, 4)).toUpperCase()

function differentArray(a, b) {
	if (a.length != b.length)
		return true
	for (let i = 0; i < a.length; i++) {
		if (a[i] !== b[i])
			return true
	}
}
